import { useState, useEffect } from "react";
import { Image, Save, Loader2, Upload, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { API_URL } from "../utils/api";

export default function HomepageImagesAdmin() {
  const [images, setImages] = useState({
    hero_image: "",
    services_image: "",
    team_image: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/website/homepage-images`);
      const data = await res.json();
      if (data.success) {
        setImages(data.images || {});
      }
    } catch (error) {
      toast.error("Kunde inte hämta bilder");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/website/homepage-images`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(images)
      });
      const data = await res.json();
      if (data.success) {
        setImages(data.images);
        toast.success("Bilder sparade!");
      } else {
        toast.error(data.detail || "Kunde inte spara");
      }
    } catch (error) {
      toast.error("Nätverksfel");
    } finally {
      setSaving(false);
    }
  };

  const imageFields = [
    {
      key: "hero_image",
      label: "Hero-bild (överst på startsidan)",
      description: "Huvudbilden som visas i hero-sektionen"
    },
    {
      key: "services_image",
      label: "Tjänster-bild",
      description: "Bilden som visas i tjänster-sektionen"
    },
    {
      key: "team_image",
      label: "Om oss-bild",
      description: "Bilden som visas i om oss-sektionen"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#255E69] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Startsidans bilder</h2>
          <p className="text-gray-500 mt-1">Hantera bilderna på startsidan</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Spara ändringar
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Tips:</p>
        <p>Ladda upp bilder i Mediabiblioteket först, kopiera sedan URL:en och klistra in här.</p>
      </div>

      <div className="space-y-8">
        {imageFields.map((field) => (
          <div key={field.key} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Preview */}
              <div className="lg:w-1/3">
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                  {images[field.key] ? (
                    <img
                      src={images[field.key]}
                      alt={field.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Image className="w-12 h-12" />
                    </div>
                  )}
                </div>
              </div>

              {/* Form */}
              <div className="lg:w-2/3 space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-900">{field.label}</label>
                  <p className="text-xs text-gray-500 mt-0.5">{field.description}</p>
                </div>
                <Input
                  value={images[field.key] || ""}
                  onChange={(e) => setImages({ ...images, [field.key]: e.target.value })}
                  placeholder="https://..."
                  className="font-mono text-sm"
                />
                {images[field.key] && (
                  <a
                    href={images[field.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#255E69] hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Öppna bild i ny flik
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
